import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import env from '../config/env';
import { getReviewSummary } from '../api/real/reviewsApi';

/**
 * Store-wide rating numbers, loaded once per visit. Nothing is shown (no stars, no "0 reviews") until the store turns
 * "Show star ratings" on in the admin; then only approved, real ratings appear.
 */
const ReviewsContext = createContext({ ratingsPublic: false, textPublic: false, ratingFor: () => null, anyRated: false });

export const ReviewsProvider = ({ children }) => {
  const [state, setState] = useState({ ratingsPublic: false, textPublic: false, byId: {} });

  useEffect(() => {
    if (env.isMock) return undefined;
    let alive = true;
    getReviewSummary().then((d) => {
      if (!alive || !d) return;
      const byId = {};
      (d.products || []).forEach((p) => {
        byId[p.productId] = { average: Number(p.average) || 0, count: Number(p.count) || 0 };
      });
      setState({ ratingsPublic: !!d.ratingsPublic, textPublic: !!d.ratingsPublic && !!d.textPublic, byId });
    }).catch(() => { /* ratings stay hidden */ });
    return () => { alive = false; };
  }, []);

  /** { average, count } of a product with at least one approved rating, else null. */
  const ratingFor = useCallback((productId) => {
    if (!state.ratingsPublic) return null;
    const r = state.byId[productId];
    return r && r.count > 0 ? r : null;
  }, [state]);

  const value = useMemo(() => ({
    ratingsPublic: state.ratingsPublic,
    textPublic: state.textPublic,
    ratingFor,
    anyRated: state.ratingsPublic && Object.keys(state.byId).length > 0,
  }), [state, ratingFor]);

  return <ReviewsContext.Provider value={value}>{children}</ReviewsContext.Provider>;
};

export const useReviews = () => useContext(ReviewsContext);
